import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { ApiError } from '@/api/client';
import { inventory, type InventoryRowDTO } from '@/api/endpoints';
import { useRun } from '@/store/run';
import { useSession, selectCoins } from '@/store/session';
import { BrightnessControl } from '@/components/ui/BrightnessControl';
import { Icon } from '@/components/ui/Icon';

/**
 * The pause overlay — the cave never navigates away.
 *
 * Before this, leaving the cave meant `exit()` (which *ends the run*) plus a
 * screen swap. That made every glance at your bag cost a descent, and it made
 * the game feel like a website with tabs rather than something you are inside.
 *
 * So this is an overlay, not a route: the corridor stays mounted and the run
 * stays alive underneath. Only "Return to surface" actually ends it, and it says
 * so plainly.
 */
export function PauseMenu({ onClose, onSurface, onMaps }: {
  onClose: () => void;
  onSurface: () => void;
  /** Open the map screen. The run stays alive underneath — see `Maps`. */
  onMaps: () => void;
}) {
  const depth = useRun((s) => s.depth);
  const zone = useRun((s) => s.zone);
  const coins = useSession(selectCoins);
  const bestDepth = useSession((s) => s.bestDepth);

  const [tab, setTab] = useState<'menu' | 'bag'>('menu');
  const [rows, setRows] = useState<InventoryRowDTO[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [confirmSurface, setConfirmSurface] = useState(false);

  /* Escape closes, like every other modal on the platform. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const loadBag = useCallback(async () => {
    setBusy(true);
    try {
      const res = await inventory.list();
      setRows(res.items);
    } catch (e) {
      setNote(e instanceof ApiError ? e.message : 'Could not open your bag.');
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { if (tab === 'bag' && rows === null) void loadBag(); }, [tab, rows, loadBag]);

  const trash = (rows ?? []).filter((r) => r.pool === 'trash' && r.quantity > 0);
  const treasure = (rows ?? []).filter((r) => r.pool === 'treasure' && r.quantity > 0);
  const haulValue = [...trash, ...treasure]
    .reduce((n, r) => n + r.sell_value * r.quantity, 0);

  return (
    <div
      className="absolute inset-0 z-[40] flex flex-col bg-black/78 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Paused"
    >
      {/* Tapping the dim area resumes — the fastest way back into the game. */}
      <button
        type="button"
        className="absolute inset-0 cursor-default"
        onClick={onClose}
        aria-label="Resume"
        tabIndex={-1}
      />

      <div className="relative mx-3 mb-3 mt-auto max-h-[86%] overflow-y-auto rounded-lg
                      border border-ui-lit bg-[linear-gradient(#2A1B10,#1A110A)]
                      shadow-[0_-8px_40px_rgba(0,0,0,.6)]">
        {/* ── header ── */}
        <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-[var(--hairline)]
                        bg-[#2A1B10]/95 px-3 py-2.5 backdrop-blur">
          <Icon name="pause" size={16} className="text-torch-core" />
          <div className="min-w-0 flex-1">
            <p className="font-pixel text-[9px] tracking-wide text-torch-core">PAUSED</p>
            <p className="text-[10px] text-ink-dim">
              {zone.name} · {depth}m{bestDepth > depth ? ` · best ${bestDepth}m` : ''}
            </p>
          </div>
          <span className="flex items-center gap-1 font-pixel text-[9px] text-coin tabular">
            <Icon name="coin" size={13} />
            {coins.toLocaleString()}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="ml-1 grid h-7 w-7 place-items-center rounded-full border
                       border-[var(--hairline)] bg-black/50 text-xs"
            aria-label="Resume"
          >
            <Icon name="close" size={13} />
          </button>
        </div>

        {/* ── tabs ── */}
        <div className="flex gap-1 px-3 pt-2.5">
          {(['menu', 'bag'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              aria-current={tab === t ? 'true' : undefined}
              className={`flex-1 rounded-sm py-1.5 font-pixel text-[7px] tracking-wide
                ${tab === t
                  ? 'bg-[rgba(255,179,71,.14)] text-torch-core'
                  : 'text-ink-dim'}`}
            >
              {t === 'menu' ? 'MENU' : 'HAUL'}
            </button>
          ))}
        </div>

        <div className="p-3">
          {tab === 'menu' ? (
            <>
              <div className="grid grid-cols-2 gap-2">
                <Tile icon={<Icon name="play" size={20} />} label="RESUME" hint="Back to digging"
                      onClick={onClose} primary />
                <Tile icon={<Icon name="bag" size={20} />} label="YOUR HAUL" hint="What you are carrying"
                      onClick={() => setTab('bag')} />
                <Tile icon={<Icon name="home" size={20} />} label="SURFACE" hint="Ends this run"
                      onClick={() => setConfirmSurface(true)} danger />
                <Tile icon={<Icon name="map" size={20} />} label="MAPS" hint="Where else you can dig"
                      onClick={onMaps} />
              </div>

              {/*
                The dimmer, here rather than only in Settings.

                A fourth tile used to sit in that grid reading SETTINGS · "Coming from
                the menu", wired to `onClose` — a labelled button that closed the menu
                and did nothing else. The one setting that actually belongs in a pause
                overlay is this one: brightness can only be judged against the cave, and
                the cave is on screen right behind this panel.
              */}
              <div className="mt-2.5 rounded-md border border-[var(--hairline)] bg-black/35 p-3">
                <BrightnessControl compact />
              </div>
            </>
          ) : (
            <>
              {/* Read-only underground. Selling is a surface action: you carry
                  rocks OUT to sell them, which is what makes surfacing a
                  decision. The server enforces it (MUST_BE_AT_SURFACE), so a
                  sell button here could only ever produce an error. */}
              <div className="mb-2.5 flex items-center gap-2.5 rounded-sm border
                              border-[var(--hairline)] bg-black/30 px-3 py-2.5">
                <Icon name="coin" size={18} className="text-coin" />
                <div className="min-w-0 flex-1">
                  <p className="font-pixel text-[7.5px] tracking-wide text-torch-core">
                    WORTH {haulValue.toLocaleString()} COINS
                  </p>
                  <p className="mt-0.5 text-[10px] leading-snug text-ink-dim">
                    Sell it back at the surface — no buyers down here.
                  </p>
                </div>
              </div>

              {treasure.length > 0 && (
                <p className="mb-1.5 font-pixel text-[6.5px] tracking-wider text-ink-dim">
                  TREASURE
                </p>
              )}
              <ul className="space-y-1">
                {[...treasure, ...trash].map((r) => (
                  <li key={r.key}
                      className="flex items-center gap-2 rounded-sm border border-[var(--hairline)]
                                 bg-black/30 px-2.5 py-1.5">
                    <span className="h-2 w-2 shrink-0 rounded-full"
                          style={{ background: r.rarity_color }} aria-hidden />
                    <span className="min-w-0 flex-1 truncate text-[12px] text-ink">{r.name}</span>
                    <span className="shrink-0 text-[10px] text-ink-dim tabular">
                      ×{r.quantity} · {(r.sell_value * r.quantity).toLocaleString()}
                    </span>
                  </li>
                ))}
              </ul>

              {rows !== null && trash.length === 0 && treasure.length === 0 && (
                <p className="py-6 text-center text-[11px] text-ink-dim">
                  Nothing in your bag yet. Break some rocks.
                </p>
              )}
              {rows === null && busy && (
                <p className="py-6 text-center text-[11px] text-ink-dim">Opening your bag…</p>
              )}
            </>
          )}

          {note && (
            <p role="status"
               className="mt-2.5 rounded-md border border-[rgba(92,203,106,.35)] bg-black/50
                          px-2.5 py-1.5 text-center text-[11px] text-ink">
              {note}
            </p>
          )}
        </div>
      </div>

      {/* Surfacing ends the run, so it asks — losing a deep descent to a mis-tap
          is the kind of thing players quit over. */}
      {confirmSurface && (
        <div className="absolute inset-0 z-[50] grid place-items-center bg-black/70 p-6">
          <div className="w-full max-w-[320px] rounded-lg border border-ui-lit
                          bg-[#2A1B10] p-4 text-center">
            <h3 className="font-pixel text-[9px] text-[var(--warn)]">LEAVE THE CAVE?</h3>
            <p className="mt-2.5 text-[11px] leading-relaxed text-ink">
              This ends the run at <b className="text-torch-core">{depth}m</b>. Your loot is
              already banked — but you will start the next descent from 0m.
            </p>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={() => setConfirmSurface(false)}
                className="flex-1 rounded-sm border border-ui-lit bg-cave-floor py-2
                           font-pixel text-[7px] text-ink"
              >
                KEEP DIGGING
              </button>
              <button
                type="button"
                onClick={onSurface}
                className="flex-1 rounded-sm border border-[rgba(224,85,63,.6)]
                           bg-[rgba(224,85,63,.25)] py-2 font-pixel text-[7px] text-[#FF9A8B]"
              >
                SURFACE
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Tile({ icon, label, hint, onClick, primary, danger, muted }: {
  icon: ReactNode; label: string; hint: string; onClick: () => void;
  primary?: boolean; danger?: boolean; muted?: boolean;
}) {
  const skin = primary
    ? 'border-[rgba(255,220,170,.5)] bg-[linear-gradient(#FFA945,#E0761C)] text-[#2A1503] shadow-[0_3px_0_-1px_#8C440C]'
    : danger
      ? 'border-[rgba(224,85,63,.45)] bg-[rgba(224,85,63,.14)] text-[#FF9A8B]'
      : `border-ui-lit bg-[linear-gradient(#3A2718,var(--cave-floor))] text-ink ${muted ? 'opacity-70' : ''}`;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-col items-start gap-0.5 rounded-sm border px-3 py-2.5 text-left
                  transition-transform active:translate-y-0.5 ${skin}`}
    >
      <span className="leading-none" aria-hidden>{icon}</span>
      <span className="mt-1 font-pixel text-[7.5px] tracking-wide">{label}</span>
      <span className="text-[9.5px] opacity-70">{hint}</span>
    </button>
  );
}
